import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Wallet, TrendingUp, Users, ArrowDownToLine,
  Receipt, LogOut, ShieldCheck, Coins, FileText, KeyRound,
} from "lucide-react";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/deposit", label: "Deposit USDT", icon: Wallet, id: "nav-deposit" },
  { to: "/plans", label: "Invest / Plans", icon: TrendingUp, id: "nav-plans" },
  { to: "/team", label: "Team & Referral", icon: Users, id: "nav-team" },
  { to: "/withdraw", label: "Withdraw", icon: ArrowDownToLine, id: "nav-withdraw" },
  { to: "/history", label: "Ledger / History", icon: Receipt, id: "nav-history" },
  { to: "/business-plan", label: "Business Plan", icon: FileText, id: "nav-business-plan" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cpOpen, setCpOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-[#0b0f16] p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-8">
          <Coins className="h-7 w-7 text-emerald-400" />
          <span className="text-xl font-extrabold tracking-tight">ERAN <span className="text-emerald-400">USD</span></span>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                           : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-amber-400 hover:bg-amber-500/10 mt-2 border border-amber-500/20">
              <ShieldCheck className="h-4 w-4" /> Admin Panel
            </NavLink>
          )}
        </nav>
        <div className="border-t border-border pt-4">
          <div className="text-xs text-slate-500 mb-2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <button data-testid="change-password-btn" onClick={() => setCpOpen(true)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors mb-3">
            <KeyRound className="h-4 w-4" /> Change Password
          </button>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between p-4 border-b border-border bg-[#0b0f16]">
          <span className="font-extrabold">ERAN <span className="text-emerald-400">USD</span></span>
          <div className="flex items-center gap-4">
            <button data-testid="change-password-btn-mobile" onClick={() => setCpOpen(true)}>
              <KeyRound className="h-5 w-5 text-slate-400" />
            </button>
            <button data-testid="logout-btn-mobile" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-5 w-5 text-slate-400" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
        <nav className="md:hidden grid grid-cols-6 border-t border-border bg-[#0b0f16] sticky bottom-0">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to}
              className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-emerald-400" : "text-slate-500"}`}>
              <l.icon className="h-4 w-4 mb-0.5" />
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
